import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Email, Phone } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const faqItems = [
  {
    question: '1. What does "bespoke clothing" mean?',
    answer:
      "Bespoke clothing refers to garments that are custom-made to fit an individual's unique measurements and preferences. Unlike ready-to-wear clothing, bespoke clothing is crafted specifically for each customer."
  },
  {
    question: '2. How does the bespoke process work?',
    answer:
      'The bespoke process begins with a consultation where preferences, measurements, and design details are discussed. Our expert designers create a pattern and construct the garment according to your specifications.'
  },
  {
    question: '3. How long does it take to receive my bespoke garment?',
    answer:
      'Timelines vary with design complexity and current workload. Generally, the process takes around 4 weeks from consultation to final delivery.'
  },
  {
    question: '4. Do you offer customisation options for design details?',
    answer:
      "Absolutely. From collar styles to pocket placements and colour palettes, we collaborate with you to ensure every detail reflects your team's identity."
  },
  {
    question: '5. Can you replicate a specific design or style?',
    answer:
      'We specialise in unique garments, but we can work with reference images or descriptions to replicate specific designs where possible.'
  },
  {
    question: '6. How do I schedule a consultation?',
    answer:
      'Contact us by email or phone and we will arrange a convenient time to discuss your bespoke requirements with a designer.'
  },
  {
    question: '7. What forms of payment do you accept?',
    answer:
      'We accept cheque, bank transfer, and cash payments. Full payment details are provided during the consultation.'
  },
  {
    question: '8. Do you offer international shipping?',
    answer:
      'Yes. Shipping costs and delivery times vary by destination—get in touch for international shipping options.'
  }
];

const promises = [
  {
    title: 'Design in the UK',
    description:
      'Your high quality playing and leisure kits are manufactured in our own factory in England, to the highest British standards.'
  },
  {
    title: '4 Week Turnaround',
    description:
      'Your order will be delivered in 4 weeks or less. In 2020 we averaged an 11-day turnaround for 2900 orders.'
  },
  {
    title: 'Quality Control',
    description:
      'All garments are quality checked throughout production, from initial manufacture through to final dispatch.'
  }
];

const ContactCTA = () => (
  <Box
    sx={{
      backgroundImage:
        'linear-gradient(135deg, rgba(0,0,0,0.88) 0%, rgba(34,34,34,0.9) 100%)',
      color: 'white',
      py: { xs: 8, md: 10 },
      mt: 8
    }}
  >
    <Container maxWidth="lg">
      <Typography variant="h6" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
        Contacts
      </Typography>
      <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 4 }}>
        For any inquiries please email or call us
      </Typography>
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Email sx={{ fontSize: 32, color: 'secondary.main' }} />
              <Typography
                component="a"
                href="mailto:info@zaror.co.uk"
                variant="h5"
                sx={{ color: 'white', textDecoration: 'none', fontWeight: 600 }}
              >
                info@zaror.co.uk
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Phone sx={{ fontSize: 32, color: 'secondary.main' }} />
              <Typography
                component="a"
                href="tel:01216901020"
                variant="h5"
                sx={{ color: 'white', textDecoration: 'none', fontWeight: 600 }}
              >
                0121 690 1020
              </Typography>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={6}
            sx={{
              p: { xs: 4, md: 5 },
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.12)'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Ready to discuss your kit?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.8 }}>
              Send us your requirements and our team will be in touch with concepts,
              pricing, and next steps.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                color="secondary"
                sx={{ fontWeight: 600, borderRadius: 2 }}
              >
                Contact Us
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

const FAQs = () => {
  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.78), rgba(0,0,0,0.65)), url('https://images.unsplash.com/photo-1521412644187-c49fa049e84d?auto=format&fit=crop&w=1650&q=80')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          py: { xs: 12, md: 14 }
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems={{ xs: 'center', md: 'flex-start' }}>
            <Typography variant="h4" sx={{ letterSpacing: 6, fontWeight: 600 }}>
              FAQ
            </Typography>
            <Typography
              variant="h2"
              component="h1"
              sx={{ fontWeight: 700, textAlign: { xs: 'center', md: 'left' } }}
            >
              Most frequent questions and answers
            </Typography>
            <Typography
              variant="h6"
              sx={{
                maxWidth: 720,
                textAlign: { xs: 'center', md: 'left' },
                opacity: 0.85
              }}
            >
              Explore common questions about our bespoke process, timelines, and services. Still need help? Contact our team any time.
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Stack spacing={2}>
          {faqItems.map((item, index) => (
            <Accordion key={item.question} defaultExpanded={index === 0}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`faq-content-${index}`}
                id={`faq-header-${index}`}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" color="text.secondary">
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Container>

      <Box sx={{ bgcolor: 'grey.100', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 700, textAlign: 'center', mb: 4, textTransform: 'uppercase' }}
          >
            Our Promise
          </Typography>
          <Grid container spacing={4}>
            {promises.map((promise) => (
              <Grid item xs={12} md={4} key={promise.title}>
                <Paper
                  elevation={4}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    {promise.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ flexGrow: 1 }}>
                    {promise.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <ContactCTA />

      <Box sx={{ py: 4, textAlign: 'center', bgcolor: 'background.default' }}>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} All Rights Reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default FAQs;
